import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Switch,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTextField } from '@personal-dashboard/ui';
import { DeleteDailyDialog } from './DeleteDailyDialog';

const GET_DAILIES = gql`
  query GetDailies($activeOnly: Boolean) {
    getDailies(activeOnly: $activeOnly) {
      id
      title
      isActive
    }
  }
`;

const CREATE_DAILY = gql`
  mutation CreateDaily($input: DailyInput!) {
    createDaily(input: $input) {
      id
      title
      isActive
    }
  }
`;

const UPDATE_DAILY = gql`
  mutation UpdateDaily($id: ID!, $input: DailyInput!) {
    updateDaily(id: $id, input: $input) {
      id
      isActive
    }
  }
`;

const DELETE_DAILY = gql`
  mutation DeleteDaily($id: ID!) {
    deleteDaily(id: $id)
  }
`;

interface LocalDaily {
  id: string;
  title: string;
  isActive: boolean;
}

interface CreateForm {
  title: string;
}

export const DailyHabitsTab: React.FC = () => {
  const createMethods = useForm<CreateForm>({ defaultValues: { title: '' } });
  const { handleSubmit, reset } = createMethods;
  const { data, loading, error, refetch } = useQuery(GET_DAILIES, { variables: { activeOnly: false } });
  const [localDailies, setLocalDailies] = useState<LocalDaily[]>([]);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [createDaily] = useMutation(CREATE_DAILY, { onCompleted: () => refetch() });
  const [updateDaily] = useMutation(UPDATE_DAILY, { onCompleted: () => refetch() });
  const [deleteDaily] = useMutation(DELETE_DAILY);

  useEffect(() => {
    if (data?.getDailies) {
      setLocalDailies(data.getDailies.map((d: any) => ({ id: d.id, title: d.title, isActive: d.isActive })));
    }
  }, [data]);

  const handleCreate = (formData: CreateForm) => {
    if (!formData.title.trim()) return;
    const tempId = `temp_${Date.now()}`;
    const optimistic: LocalDaily = { id: tempId, title: formData.title.trim(), isActive: true };
    setLocalDailies((prev) => [...prev, optimistic]);
    reset({ title: '' });
    createDaily({ variables: { input: { title: formData.title.trim(), isActive: true } } }).catch((err) => {
      console.error('Create daily failed:', err);
      setLocalDailies((prev) => prev.filter((d) => d.id !== tempId));
    });
  };

  const handleToggleActive = async (daily: LocalDaily) => {
    const prev = localDailies;
    setLocalDailies((prev) =>
      prev.map((d) => (d.id === daily.id ? { ...d, isActive: !d.isActive } : d))
    );
    try {
      await updateDaily({
        variables: {
          id: daily.id,
          input: { title: daily.title, isActive: !daily.isActive }
        }
      });
    } catch (err) {
      console.error('Toggle failed:', err);
      setLocalDailies(prev);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteLoading(true);
    const prev = localDailies;
    setLocalDailies((prev) => prev.filter((d) => d.id !== deleteTargetId));
    setDeleteTargetId(null);
    try {
      await deleteDaily({ variables: { id: deleteTargetId } });
    } catch (err) {
      console.error('Delete failed:', err);
      setLocalDailies(prev);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) return <Typography>Loading daily habits...</Typography>;
  if (error) return <Alert severity="error">Failed to load daily habits: {error.message}</Alert>;

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Daily Habits
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          These tasks will repeat every single day in your Day View.
        </Typography>

        <Box component="form" onSubmit={handleSubmit(handleCreate)} sx={{ display: 'flex', gap: 1, mb: 4 }}>
          <FormProvider {...createMethods}>
            <FormTextField
              name="title"
              size="small"
              fullWidth
              placeholder="e.g., Hit the gym, 2L Water, Meditate..."
            />
          </FormProvider>
          <Button type="submit" variant="contained" startIcon={<AddIcon />}>
            Add
          </Button>
        </Box>

        {!data?.getDailies || data.getDailies.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No daily habits yet. Add one above.
          </Typography>
        ) : (
          <List>
            {localDailies.map((daily: LocalDaily) => (
              <React.Fragment key={daily.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Switch
                        checked={daily.isActive}
                        onChange={() => handleToggleActive(daily)}
                        color="primary"
                      />
                      <IconButton edge="end" onClick={() => setDeleteTargetId(daily.id)} size="small" sx={{ ml: 1 }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={daily.title}
                    primaryTypographyProps={{
                      sx: { opacity: daily.isActive ? 1 : 0.5 }
                    }}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <DeleteDailyDialog
        open={!!deleteTargetId}
        loading={deleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTargetId(null)}
      />
    </Box>
  );
};
